import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { t } from '@web-solutions/module-localization';

import { ApplePayButton, PaymentSystem } from '@web-solutions/react-billing';
import { ApplePayTruegateContainer } from '@web-solutions/react-billing/truegate/ApplePayContainer';
import { selectPaymentSystem } from '@web-solutions/core/store/billing/selectors';
import { enrichStripeIntroProduct } from '@web-solutions/core/utils/stripe-payment';

import classes from './style.module.scss';
import './style.scss';

import PropTypes from "prop-types";

const ApplePay = ({
  activeProduct,
  className,
  onApplePayClick,
  onApplePaySubmit,
  onApplePaySuccess,
  onApplePayError,
  onRequireEmail,
  onAvailabilityChange,
}) => {
  const email = useSelector((state) => state.profile.email);
  const paymentSystem = useSelector(selectPaymentSystem);
  const orderDetails = useSelector((state) => state.billing.orderDetails);

  const APP_NAME = t('core.appName');

  // Stripe charges the intro one-time amount today ($0.99 hardcoded) and
  // then the subscription. Without enrichment ApplePayButton would read
  // product.trial_price_amount from Stripe (e.g. $4.99) and show the wrong
  // total in the native sheet — so force the intro override here too.
  const isStripe =
    paymentSystem === PaymentSystem.STRIPE || Boolean(activeProduct?.stripeId);
  const introAmount = activeProduct?.trialPriceAmount ? Number(activeProduct.trialPriceAmount) : (activeProduct?.amount ? Number(activeProduct.amount) : (activeProduct?.priceAmount ? Number(activeProduct.priceAmount) : undefined));
  const productForApplePay = isStripe
    ? (enrichStripeIntroProduct(activeProduct, introAmount) || activeProduct)
    : activeProduct;

  const handleSubmit = (formData) => {
    onApplePaySubmit({ email, ...formData });
  };

  // Truegate Apple Pay uses its own SDK (window.truegateSdk) and needs the
  // intent transactionId from orderDetails. The generic ApplePayButton/
  // useApplePay flow only handles Stripe/Recurly/Solidgate, so for Truegate
  // we render the dedicated container instead.
  if (paymentSystem === PaymentSystem.TRUEGATE) {
    if (!orderDetails) {
      return null;
    }

    return (
      <ApplePayTruegateContainer
        options={{ orderDetails }}
        email={email}
        product={activeProduct}
        // The SDK's APPLE_PAY_READY is Truegate's "the sheet can open here".
        onReady={() => onAvailabilityChange?.(true)}
        onClick={() => {
          if (!email) {
            onRequireEmail?.();
            return;
          }
          onApplePayClick?.();
        }}
        onSubmit={(data) => {
          onApplePaySubmit?.({
            email,
            method: 'applepay',
            paymentSystem: PaymentSystem.TRUEGATE,
            ...data,
          });
        }}
        onSuccess={(purchase) => {
          onApplePaySuccess?.({
            ...purchase,
            paymentSystem: PaymentSystem.TRUEGATE,
            method: 'applepay',
          });
        }}
        onError={(err) => {
          onApplePayError?.({
            ...(err || {}),
            paymentSystem: PaymentSystem.TRUEGATE,
            method: 'applepay',
          });
        }}
      />
    );
  }

  return (paymentSystem !== PaymentSystem.SOLIDGATE && paymentSystem !== PaymentSystem.PADDLE)
    || !!process.env.REACT_APP_APPLE_PAY_H2H
    ? (
      <ApplePayButton
        product={productForApplePay}
        label={APP_NAME}
        className={classNames(classes.btn, className)}
        onClick={onApplePayClick}
        onSubmit={handleSubmit}
        // disabled={!email}
        email={email}
        onRequireEmail={onRequireEmail}
        onAvailabilityChange={onAvailabilityChange}
      />
    ) : null;
};

ApplePay.propTypes = {
  activeProduct: PropTypes.object.isRequired,
  className: PropTypes.any,
  onApplePayClick: PropTypes.func,
  onApplePaySubmit: PropTypes.func.isRequired,
  onApplePaySuccess: PropTypes.func,
  onApplePayError: PropTypes.func,
  onRequireEmail: PropTypes.func,
  onAvailabilityChange: PropTypes.func,
};

ApplePay.defaultProps = {
  className: null,
  onApplePayClick: null,
  onApplePaySuccess: null,
  onApplePayError: null,
  onRequireEmail: null,
  onAvailabilityChange: null,
};

export default ApplePay;
